import {
  REPS_BY_LEVEL,
  WORK_TIME_BY_LEVEL_S,
  DEFAULT_REPS,
  DEFAULT_WORK_TIME_S,
} from "./config";
import { buildCapabilitiesSection } from "../../utils/capabilityPrompt";
import { buildTrainingHistorySection } from "../../utils/trainingHistory";
import { buildTrainingPreferencesSection } from "../../utils/trainingPreferencesPrompt";
import { buildMuscleVolumeSection } from "../../utils/muscleVolume";
import { EXERCISES } from "../../config/exercises";

// --- PROMPT GENERATION LOGIC ---

// Terse structural rules — the LLM already knows the fitness concepts.
// Focus on encoding rules for our JSON schema, not explaining the workout style.
const STYLE_INSTRUCTIONS = {
  // --- HIGH INTENSITY / FUNCTIONAL ---
  // Original Tabata was ~170% VO2max on a bike: only all-out-able movements
  // qualify, and a second block cannot be done at that intensity
  // (docs/training_science.md §3).
  tabata: `Tabata: Group(value=8, unit="rounds"). Exactly 2 children per round: Work(value=20, unit="s", autoStart=true) + Rest(value=10, unit="s", autoStart=true). For multiple sets, place a between-set Rest OUTSIDE the Tabata Group. A Tabata is ALL-OUT work, so the movement must allow a safe maximal effort for 20 s: bike, rower, ski erg, sprints, jumps, burpees, kettlebell swings. NEVER a heavy or technical loaded lift (no barbell squats, cleans, thrusters). intensityTarget is all-out ("Max output every interval"). At most ONE Tabata block per session — a second cannot be done at true intensity.`,

  emom: `EMOM: a self-paced WINDOW format — start the work at the top of a fixed window, and whatever time is left once you finish IS the rest. Finish faster, rest longer. Shape: Group(unit="rounds", value=number of WINDOWS) whose ONLY child is a time-capped Group(unit="s", value=window_seconds) holding the station(s) as a checklist. NEVER author a Rest node anywhere in an EMOM — the leftover of the window is the rest, and prescribing it is wrong. Work MUST be COUNTED, never timed: Work(unit="reps") for movements, or for ergo machines (row/bike/ski/run) either a calorie target — Work(unit="cal", value=calories) — or a distance, emitted as Work(unit="m", value=metres), which the runtime converts to a pace estimate. A timed station makes it a circuit, not an EMOM. The only exception is an isometric hold or loaded carry (plank, hang, farmer carry), where the hold itself is the prescription. Window = 60s (EMOM), or whole minutes for bigger rounds — E2MOM/E3MOM/E4MOM — which is the right choice for heavy lifting or multi-station rounds. Size it so the work fills roughly 60-85% of the window: the work must be completable inside it, but a window that is two-thirds idle is not a training stimulus (reserve those for near-maximal lifting, where the recovery is the point). Label the block with its total duration and format ("12-Min E3MOM"), and make rounds × window equal that total exactly.`,

  amrap: `AMRAP: Group(value=time_cap_in_minutes, unit="m"). Children = sequence of Work(unit="reps") nodes. Do NOT include Rest nodes unless specifically requested. The user repeats the children until the time cap. value is the requested cap itself — NEVER an estimate of how long the movements take — and if the label states a duration (e.g. "12 Min AMRAP"), value MUST equal it.`,

  // Anaerobic capacity and VO2max are different stimuli (Galpin; Helgerud
  // 2007) — a block that averages them trains neither well.
  hiit: `HIIT: Group(unit="rounds"). Alternate Work(unit="s", autoStart=true) and Rest(unit="s", autoStart=true). For multiple sets, place a between-set Rest OUTSIDE the HIIT Group. Each HIIT block trains ONE target — pick it and name it in the label, never blend the two in one block:
  - ANAEROBIC ("Anaerobic Intervals"): Work 20-90 s ALL-OUT, Rest at least as long as the work and long enough to recover (1:1 to 1:3), about 4-6 min of all-out work in total across the block. intensityTarget "All-out".
  - AEROBIC POWER ("VO2max Intervals"): Work 180-240 s at 90-95% of max heart rate (hard but sustainable for the whole interval), Rest 120-180 s easy/active, 3-4 rounds. intensityTarget "90-95% HRmax" or "RPE 8-9".
Use movements that can be pushed hard safely for the full interval — ergs, running, cycling, simple bodyweight or light kettlebell work — not heavy or technical lifts.`,

  circuit: `Circuit: Group(value=rounds, unit="rounds"). Children = exercise sequence (reps or s) performed back-to-back with NO Rest between stations, then a SINGLE Rest(unit="s") as the LAST child (round-boundary recovery). autoStart=true on all. Do NOT rest between different stations.`,

  functional: `Functional: Circuit-style Group(unit="rounds"). Mix Work(unit="reps") and Work(unit="s") performed back-to-back. End each round with a SINGLE Rest (round-boundary). Do NOT rest between the different movements within a round. Include compound movements.`,

  // --- STRENGTH / POWER / HYPERTROPHY ---
  // ACSM 2026 (>=80% 1RM, key lifts early); Galpin 3-5 protocol; Robinson
  // 2024 (strength does not need failure). See docs/training_science.md §2.
  strength: `Strength: ONE Group per exercise, Group(unit="sets", value=number of sets). Each set: Work(unit="reps", value=3-6) at >=80% 1RM + Rest(unit="s", 150-300s, autoStart=true) — this Rest is the inter-SET recovery for the SAME exercise; use the top of the range (up to 300 s) after the heaviest sets. 3-5 working sets for the main lift, 2-3 for secondary lifts. Main compound lifts come FIRST in the block. Stop every set 2-4 reps short of failure — strength does not need failure. Do NOT add a Rest between two different exercise Groups; the runtime injects the changeover automatically.`,

  // ACSM 2026 (30-70% 1RM, low total volume); Pareja-Blanco 2017 (stop
  // before speed drops). See docs/training_science.md §2.
  power: `Power: ONE Group per exercise, Group(unit="sets", value=number of sets). Each set: Work(unit="reps", value=3-5) with explosive intent + Rest(unit="s", 120-300s, autoStart=true) for FULL recovery. Movements: jumps, throws, olympic-lift variations, or fast concentric compounds at 30-70% 1RM. Keep total volume low: no more than ~24 explosive reps per exercise in a session. NEVER take a set near failure — end the set as soon as speed drops; quality and bar speed over fatigue. intensityTarget should express explosive intent (e.g. "Max bar speed", "Max height").`,

  // Singer 2024 (>60 s rest), Robinson 2024 (closer to failure grows more),
  // Wolf 2023 (lengthened position), ACSM 2026 (eccentric emphasis).
  hypertrophy: `Hypertrophy: ONE Group per exercise, Group(unit="sets", value=number of sets). Each set: Work(unit="reps", value=6-15, mostly 8-12) + Rest(unit="s", autoStart=true) — inter-SET recovery for the SAME exercise: 90-120s for compound lifts, 120-180s after heavy compound lifts, 60-90s for single-joint (isolation) exercises such as curls, raises and extensions. Take sets 0-3 reps short of failure, the last set closest. Favour movements that load the muscle in its STRETCHED position (e.g. Romanian deadlift, deep squat, incline curl, overhead triceps extension) and cue a controlled 2-3 s lowering. Do NOT add a Rest between different exercise Groups.`,

  superset: `Superset: Group(unit="sets", value=number of sets). Children = 2 or 3 Work(unit="reps") nodes of opposing muscle groups performed back-to-back with NO Rest between them, followed by a SINGLE Rest(unit="s") at the END of the round only. Do NOT place a Rest between the paired exercises.`,

  // --- ENDURANCE / RECOVERY ---
  conditioning: `Conditioning: Group(unit="rounds"). Work(unit="s", 60-180s) + Rest(unit="s", 15-30s). Sustained moderate effort.`,

  // Most aerobic work should be easy (~80/20, Seiler; Oliveira 2024); easy
  // means conversational / nasal breathing (Galpin). §3 of the science doc.
  cardio: `Cardio: Steady-state = single Work(unit="m"). Intervals = Group(unit="rounds") with Work(unit="m"|"s") + Rest(unit="s"). autoStart=true. Default to EASY steady-state: a conversational pace the user could hold while breathing through the nose (Zone 2) — intensityTarget e.g. "Zone 2 - conversational, nasal breathing". About 80% of aerobic training should be easy, so use intervals only when the session calls for intensity (the goal, readiness "Feeling Great", or no hard session in recent history), and keep them clearly hard rather than moderately uncomfortable.`,

  flexibility: `Flexibility: Group of holds. Work(unit="s", 30-60s, autoStart=true) + Rest(unit="s", 5-10s).`,

  recovery: `Recovery: Work(unit="s", 30-90s, autoStart=true). Gentle movements, minimal rest.`,

  // --- MIXED ---
  // Fallback only. "mixed" is resolved into concrete styles before the prompt
  // is built (utils/mixedStyle.js), precisely because this instruction is a
  // vibe rather than a structure and the model answers it the same way every
  // time. It survives as the landing spot for an unrecognised style string.
  mixed: `Mixed: Combine different Group types to create complex sessions. You MUST nest Groups within Groups when necessary (e.g., nesting a Tabata Group or AMRAP inside a larger Circuit Group) — Groups inside Groups only, never a step inside another step. Vary exercise types between reps and timed efforts.`,
};

// Every style the prompt can structurally describe. Exported so the Mixed
// archetype table can be checked against it in tests — an archetype naming a
// style with no spec would silently degrade to the vague fallback above.
export const STYLE_KEYS = Object.keys(STYLE_INSTRUCTIONS);

const getStyleInstructions = (workoutStyle) =>
  STYLE_INSTRUCTIONS[String(workoutStyle || "").toLowerCase()] ||
  STYLE_INSTRUCTIONS.mixed;

const generateDynamicExample = (workoutStyle, userProfile) => {
  const { availableEquipment, fitnessLevel } = userProfile;

  // 1. Dynamic Exercise Selection
  const hasEq =
    availableEquipment && availableEquipment.toLowerCase() !== "none";
  const exercises = {
    push: hasEq ? "Dumbbell Press" : "Push-ups",
    legs: hasEq ? "Goblet Squats" : "Bodyweight Squats",
    pull: hasEq ? "Bent-over Rows" : "Superman Holds",
    full: hasEq ? "Dumbbell Thrusters" : "Burpees",
  };

  // 2. Dynamic Difficulty Scaling
  const reps = REPS_BY_LEVEL[fitnessLevel] || DEFAULT_REPS;
  const workTime = WORK_TIME_BY_LEVEL_S[fitnessLevel] || DEFAULT_WORK_TIME_S;

  // 3. Build the Native JS Object
  let exampleObject;

  switch (workoutStyle.toLowerCase()) {
    case "tabata":
      exampleObject = {
        id: "ex-tabata",
        label: "Tabata",
        type: "Group",
        value: 8,
        unit: "rounds",
        children: [
          {
            // Always an all-out-able movement, never a loaded lift — the
            // Tabata spec forbids heavy or technical lifts.
            id: "t-work",
            label: "Burpees",
            type: "Work",
            value: 20,
            unit: "s",
            intensityTarget: "All-out - max output every interval",
            autoStart: true,
          },
          {
            id: "t-rest",
            label: "Rest",
            type: "Rest",
            value: 10,
            unit: "s",
            autoStart: true,
          },
        ],
      };
      break;

    case "emom":
      // 10 one-minute windows = the 10 minutes the label promises. The window
      // is a self-paced countdown holding counted work — no Rest node, because
      // the remainder of the window IS the rest (issue #63).
      exampleObject = {
        id: "ex-emom",
        label: "10-Min EMOM",
        type: "Group",
        value: 10,
        unit: "rounds",
        children: [
          {
            id: "e-window",
            label: "EMOM Window",
            type: "Group",
            value: 60,
            unit: "s",
            children: [
              {
                id: "e-work",
                label: `${exercises.full}`,
                type: "Work",
                value: reps,
                unit: "reps",
                intensityTarget: "75% 1RM or RPE 7",
              },
            ],
          },
        ],
      };
      break;

    case "amrap":
      exampleObject = {
        id: "ex-amrap",
        label: "12 Min AMRAP",
        type: "Group",
        value: 12,
        unit: "m",
        children: [
          {
            id: "a-1",
            label: exercises.legs,
            type: "Work",
            value: reps,
            unit: "reps",
            intensityTarget: "Pace yourself, sustained RPE 7",
            autoStart: true,
          },
          {
            id: "a-2",
            label: exercises.push,
            type: "Work",
            value: reps,
            unit: "reps",
            intensityTarget: "Pace yourself, sustained RPE 7",
            autoStart: true,
          },
        ],
      };
      break;

    case "superset":
      exampleObject = {
        id: "ex-super",
        label: "Push/Pull Superset",
        type: "Group",
        value: 3,
        unit: "rounds",
        children: [
          {
            id: "s-1",
            label: exercises.push,
            type: "Work",
            value: reps,
            unit: "reps",
            intensityTarget: "RIR 2 - Leave 2 reps in reserve",
            autoStart: true,
          },
          {
            id: "s-2",
            label: exercises.pull,
            type: "Work",
            value: reps,
            unit: "reps",
            intensityTarget: "RIR 2 - Leave 2 reps in reserve",
            autoStart: true,
          },
          {
            id: "s-rest",
            label: "Set Rest",
            type: "Rest",
            value: 90,
            unit: "s",
            autoStart: true,
          },
        ],
      };
      break;

    // Straight-set styles each get their own example. They used to fall through
    // to the generic default (3 rounds, 30 s rest, RPE 8), which contradicted
    // every one of their specs.
    case "strength":
      exampleObject = {
        id: "ex-strength",
        label: hasEq ? "Dumbbell Bench Press" : "Assisted Pistol Squat",
        type: "Group",
        value: 4,
        unit: "sets",
        children: [
          {
            id: "st-1",
            label: hasEq ? "Dumbbell Bench Press" : "Assisted Pistol Squat",
            type: "Work",
            value: 5,
            unit: "reps",
            ...(hasEq ? { intensityPct: 82 } : {}),
            intensityTarget: "RIR 2-3 - two or three good reps left",
          },
          {
            id: "st-rest",
            label: "Set Rest",
            type: "Rest",
            value: 180,
            unit: "s",
            autoStart: true,
          },
        ],
      };
      break;

    case "hypertrophy":
      exampleObject = {
        id: "ex-hypertrophy",
        label: hasEq ? "Dumbbell Romanian Deadlift" : "Single-leg Romanian Deadlift",
        type: "Group",
        value: 3,
        unit: "sets",
        children: [
          {
            id: "hy-1",
            label: hasEq ? "Dumbbell Romanian Deadlift" : "Single-leg Romanian Deadlift",
            type: "Work",
            value: 10,
            unit: "reps",
            intensityTarget: "RIR 1-2 - controlled 3 s lowering",
          },
          {
            id: "hy-rest",
            label: "Set Rest",
            type: "Rest",
            value: 105,
            unit: "s",
            autoStart: true,
          },
        ],
      };
      break;

    case "power":
      exampleObject = {
        id: "ex-power",
        label: "Box Jump",
        type: "Group",
        value: 4,
        unit: "sets",
        children: [
          {
            id: "pw-1",
            label: "Box Jump",
            type: "Work",
            value: 3,
            unit: "reps",
            intensityTarget: "Max height - stop if jumps slow",
          },
          {
            id: "pw-rest",
            label: "Set Rest",
            type: "Rest",
            value: 150,
            unit: "s",
            autoStart: true,
          },
        ],
      };
      break;

    case "hiit":
      exampleObject = {
        id: "ex-hiit",
        label: "Anaerobic Intervals",
        type: "Group",
        value: 6,
        unit: "rounds",
        children: [
          {
            id: "hi-1",
            label: exercises.full,
            type: "Work",
            value: 30,
            unit: "s",
            intensityTarget: "All-out",
            autoStart: true,
          },
          {
            id: "hi-rest",
            label: "Easy Recovery",
            type: "Rest",
            value: 60,
            unit: "s",
            autoStart: true,
          },
        ],
      };
      break;

    case "circuit":
    case "functional":
      exampleObject = {
        id: "ex-circuit",
        label: "Functional Circuit",
        type: "Group",
        value: 3,
        unit: "rounds",
        children: [
          {
            id: "c-1",
            label: exercises.legs,
            type: "Work",
            value: workTime,
            unit: "s",
            intensityTarget: "RPE 7 - Challenging but sustainable",
            autoStart: true,
          },
          {
            id: "c-2",
            label: exercises.push,
            type: "Work",
            value: workTime,
            unit: "s",
            intensityTarget: "RPE 7 - Challenging but sustainable",
            autoStart: true,
          },
          {
            id: "c-rest",
            label: "Rest",
            type: "Rest",
            value: 60,
            unit: "s",
            intensityTarget: "Active recovery, focus on breathing",
            autoStart: true,
          },
        ],
      };
      break;

    default: // Mixed / standard default
      exampleObject = {
        id: "ex-mixed",
        label: "Main Block",
        type: "Group",
        value: 3,
        unit: "rounds",
        children: [
          {
            id: "m-1",
            label: exercises.legs,
            type: "Work",
            value: reps,
            unit: "reps",
            intensityTarget: "RPE 8",
            autoStart: true,
          },
          {
            id: "m-2",
            label: "Rest",
            type: "Rest",
            value: 30,
            unit: "s",
            autoStart: true,
          },
        ],
      };
  }

  // 4. Safely stringify the object to guarantee perfect JSON syntax in the prompt
  return JSON.stringify(exampleObject, null, 2);
};

// --- PROMPT GENERATION LOGIC ---

// Default block order for sequencing a multi-style session: strength/power/skill
// while fresh, metabolic/conditioning later, mobility/recovery to close.
// How to load inside a chosen format (#83 L4), per goal. The format's own spec
// sets the structure; these set reps, load and effort within it, using the
// same evidence-based ranges as the goals' own style specs.
const GOAL_LOADING = {
  strength:
    "Strength: heavy — 3-6 reps per station at >=80% 1RM, 2-4 reps short of failure, compound lifts. In an EMOM use 2-3 minute windows (E2MOM/E3MOM) so each heavy set gets real recovery.",
  hypertrophy:
    "Hypertrophy: 6-15 reps per station, 0-3 reps short of failure, favouring movements that load the muscle in its stretched position.",
  power:
    "Power: 3-5 explosive reps at 30-70% 1RM, or jumps and throws; end each set as soon as speed drops, never near failure, with long windows or rests.",
  hiit: "Hard intervals: all-out efforts on movements that can be pushed safely to maximum.",
  conditioning:
    "Conditioning: hard but repeatable effort that can be held across every round; full-body and erg movements.",
  cardio:
    "Cardio: an easy, conversational pace; continuous movement rather than hard efforts.",
  flexibility:
    "Mobility: controlled mobility drills and holds, no fatigue.",
  recovery: "Active recovery: easy, low-intensity movement only.",
};

const FORMAT_NAMES = {
  circuit: "a CIRCUIT",
  emom: "an EMOM",
  amrap: "an AMRAP",
  tabata: "a TABATA",
};

// At or under this many minutes the prompt adds the time-efficient training
// rules (docs/training_science.md §5).
const SHORT_SESSION_MINUTES = 30;

// From this age the prompt asks for power and balance work (WHO 2020 older
// adult guidance; docs/training_science.md §6).
const OLDER_ADULT_AGE = 65;

const STYLE_BLOCK_ORDER = {
  power: 1, // explosive work first, while freshest
  strength: 1,
  hypertrophy: 2,
  functional: 3,
  superset: 3,
  circuit: 4,
  hiit: 5,
  tabata: 5,
  emom: 5,
  amrap: 5,
  conditioning: 6,
  cardio: 7,
  flexibility: 9,
  recovery: 10,
};

const buildPrompt = (userProfile) => {
  const {
    primaryGoal,
    workoutStyle,
    workoutStyles,
    includeWarmup,
    warmupDuration = 5,
    includeCooldown,
    cooldownDuration = 5,
    autoWarmup = true,
    autoCooldown = true,
    // On Auto, only add a warm-up/cool-down once the session reaches this many
    // minutes (0 = always). Default 0 keeps any caller that omits them unchanged.
    autoWarmupThreshold = 0,
    autoCooldownThreshold = 0,
    // On Auto, a length the model should aim for (0 = size it freely).
    warmupPreferredDuration = 0,
    cooldownPreferredDuration = 0,
    fitnessLevel,
    trainingExperience,
    birthYear,
    targetMuscles,
    avoidedAreas,
    availableEquipment,
    timeAvailable,
    injuryLimitationNotes, // From the user's saved profile (e.g., "Herniated L4")
    readiness = "Normal", // From the UI toggle
    sessionNotes, // From the UI text area (e.g., "Legs are stiff today")
    intensityPreference = "RPE (1-10 scale)",
    skipLastRest = true,
    performanceCapabilities = [],
    recentTrainingHistory = [],
    historyLookbackDays = 7,
    weeklyMuscleSets = null, // hard sets per muscle, last 7 days (#83 L1)
    programmeContext, // programme-slot generation only (#54)
    // The shape a selector (Surprise me / What's next) chose, with the
    // instruction for how its blocks relate (utils/mixedStyle.js, #83).
    sessionShape,
    // No faff (#85): quick to set up, minimal kit and load changes.
    noFaff = false,
    // A chosen format (circuit/emom/amrap/tabata) and the goals to load
    // inside it (utils/sessionFormat.js, #83 L4).
    formatStyle = null,
    formatGoals = [],
    // Standing profile preferences for how a session is put together.
    restStyle,
    sessionPace,
    trainingPreferences,
  } = userProfile;

  const age = birthYear ? new Date().getFullYear() - birthYear : null;
  // All selected styles, ordered for sensible sequencing across the session.
  const styles = (
    Array.isArray(workoutStyles) && workoutStyles.length
      ? workoutStyles
      : [workoutStyle || "mixed"]
  ).filter(Boolean);
  const orderedStyles = [...styles].sort(
    (a, b) =>
      (STYLE_BLOCK_ORDER[a?.toLowerCase()] ?? 5) -
      (STYLE_BLOCK_ORDER[b?.toLowerCase()] ?? 5),
  );
  const isMultiStyle = orderedStyles.length > 1;

  // Concurrent training: same-session endurance work costs explosive power
  // most (Schumann 2022), and running interferes more than cycling. M2.
  const loadsLegsOrPower = orderedStyles.some((s) =>
    ["strength", "power", "hypertrophy"].includes(String(s).toLowerCase()),
  );
  const METABOLIC = [
    "hiit",
    "tabata",
    "conditioning",
    "circuit",
    "amrap",
    "emom",
    "functional",
    "cardio",
  ];
  const hasMetabolicFinisher = orderedStyles.some((s) =>
    METABOLIC.includes(String(s).toLowerCase()),
  );
  const concurrentRule =
    isMultiStyle && loadsLegsOrPower && hasMetabolicFinisher
      ? `
  MIXING LIFTING AND CONDITIONING: after heavy lower-body lifting or a Power block, the conditioning block should use cycling, rowing, ski erg or upper-body work rather than running, jumping or bounding — running and plyometrics add the most leg fatigue and interfere most with strength and power. Never place hard conditioning before a Power block.`
      : "";

  const styleInstructions = isMultiStyle
    ? orderedStyles.map((s) => `**${s}** — ${getStyleInstructions(s)}`).join("\n\n")
    : getStyleInstructions(orderedStyles[0]);
  const example = generateDynamicExample(orderedStyles[0], userProfile);
  // Time Available is the FULL-session budget: warm-up + main work + cool-down
  // must all fit within it. The model apportions time across blocks.
  //
  // Stated as a BAND, not a ceiling. Every phrasing here used to be an upper
  // bound ("hard stop", "stay within", "kept inside"), and a model that answers
  // a 30 min request with one exercise satisfies all of them — which is exactly
  // what happened: a 90-second "30 minute" session. A budget with no floor is
  // not a budget. The floor is deliberately close to the target: the failure
  // mode in practice is under-filling by an order of magnitude, not by 20%.
  const budgetFloorMinutes = Math.max(1, Math.round(timeAvailable * 0.9));
  const timeBudgetSummary = `Full-session budget: ${timeAvailable} min TOTAL, covering warm-up, all main work and cool-down. This is a target to FILL, not merely a ceiling to stay under — the finished session must run between ${budgetFloorMinutes} and ${timeAvailable} min. Finishing well short of ${budgetFloorMinutes} min is as wrong as overrunning.`;
  const durationConstraint = `Total full-session duration, including any warm-up and cool-down, must land between ${budgetFloorMinutes} and ${timeAvailable} min.`;
  const restFilterNote = skipLastRest
    ? " Note: the runtime automatically skips trailing rest phases at the end of each round/set, so do not count the final rest period per round toward the time budget."
    : "";

  // Construct clear structural requirements for the LLM
  let structureReqs = [];

  // Warm-up: auto-sized (default) → the model picks a sensible duration inside the
  // total budget, but only when the session reaches the user's threshold; manual →
  // fixed minutes; off → none.
  // Static stretching in a warm-up costs ~1% strength under 60 s per muscle
  // and ~5% beyond it (Behm 2016) — docs/training_science.md §4.
  const warmupStretchRule = `  Any static stretch in the warm-up is held under 60 s per muscle and followed by dynamic movement; prefer dynamic mobility and ramp-up sets over static holds.`;

  if (autoWarmup) {
    const belowThreshold =
      autoWarmupThreshold > 0 && timeAvailable < autoWarmupThreshold;
    if (belowThreshold) {
      structureReqs.push(
        `- Do NOT include a Warm-up Group. This ${timeAvailable} min session is below the user's ${autoWarmupThreshold} min auto warm-up threshold, so start directly with the main workout.`,
      );
    } else if (warmupPreferredDuration > 0) {
      structureReqs.push(
        `- First Group: a Warm-up of about ${warmupPreferredDuration} min — the user's preferred length. Trim it only if it cannot fit the total time budget.`,
      );
    } else {
      structureReqs.push(
        `- First Group: a Warm-up, with a duration you size appropriately for this session length and intensity (kept inside the total time budget).`,
      );
    }
    // Warm-up length tracks intensity, not session length: heavy/explosive work
    // needs neural + soft-tissue prep, whereas the opening minutes of an easy
    // long session are themselves the warm-up. Only said when there IS a
    // warm-up — after "do not include one" it was a contradiction.
    if (!belowThreshold) {
      structureReqs.push(
        `  Scale it to INTENSITY, not duration: heavy, explosive or near-maximal work needs the most preparation (neural priming, soft-tissue and joint prep, ramping toward the first working load), while low-intensity long-duration work needs least — there the opening minutes double as the warm-up, so keep it brief.`,
        warmupStretchRule,
      );
    }
  } else if (includeWarmup && warmupDuration > 0) {
    structureReqs.push(
      `- First Group: Warm-up (~${warmupDuration} min)`,
      warmupStretchRule,
    );
  } else {
    structureReqs.push(
      `- Do NOT include a Warm-up Group. Start directly with the main workout.`,
    );
  }

  // Main work: one DISTINCT block per selected style for multi-style sessions,
  // sequenced physiologically; otherwise a single style block.
  if (isMultiStyle) {
    structureReqs.push(
      `- Main work = ${orderedStyles.length} DISTINCT sequential blocks, one per selected style, planned as one coherent session (do NOT blend them into a single undifferentiated block):`,
    );
    orderedStyles.forEach((s, i) => {
      structureReqs.push(`    ${i + 1}. ${s} block`);
    });
    structureReqs.push(
      `  Sequence them physiologically: strength/power/skill while fresh, metabolic/HIIT/conditioning as finishers, mobility/recovery to close (light cardio may open as a primer). Apportion the main-work time across the blocks.${concurrentRule}`,
    );
    // Where the second block GOES is the part prose does not settle. A block
    // authored inside the previous block's children runs once per round of it,
    // so a 14-minute block inside an 8-round block bills 112 minutes — the
    // shape is repaired downstream, but showing the layout stops it being
    // authored. Siblings in the ROOT's children, side by side.
    structureReqs.push(
      `  BLOCKS ARE SIBLINGS. Every main block is its own entry in the ROOT's \`children\` array, side by side. NEVER place one block inside another block's \`children\` — a block nested inside a Group that runs N rounds is performed N times, so a 14-minute block inside an 8-round block costs 112 minutes, not 14. Correct shape:
    {"id":"root","type":"Group","unit":"workout","value":1,"children":[
      {"id":"b1","label":"16-Min E2MOM - Push","type":"Group","unit":"rounds","value":8,"children":[ ...block 1 only... ]},
      {"id":"b2","label":"14-Min E2MOM - Pull","type":"Group","unit":"rounds","value":7,"children":[ ...block 2 only... ]}
    ]}
  That session totals 30 min (16 + 14). The same two blocks with the second nested inside the first total 134 min — the arithmetic in your own labels is what tells you the nesting is wrong.`,
    );
  } else {
    structureReqs.push(
      `- Main ${orderedStyles[0]} Workout Blocks (bulk of the session)`,
    );
  }

  // A selector-chosen shape is one design, not a list of styles: say how its
  // blocks relate so it does not read as two workouts stapled together.
  if (sessionShape?.name && sessionShape?.glue) {
    structureReqs.push(
      `- Session shape: "${sessionShape.name}". ${sessionShape.glue}`,
    );
  }

  let hasCooldown = true;
  if (autoCooldown) {
    if (autoCooldownThreshold > 0 && timeAvailable < autoCooldownThreshold) {
      hasCooldown = false;
      structureReqs.push(
        `- Do NOT include a Cool-down Group. This ${timeAvailable} min session is below the user's ${autoCooldownThreshold} min auto cool-down threshold, so end immediately after the main blocks.`,
      );
    } else if (cooldownPreferredDuration > 0) {
      structureReqs.push(
        `- Last Group: a Cool-down of about ${cooldownPreferredDuration} min — the user's preferred length. Trim it only if it cannot fit the total time budget.`,
      );
    } else {
      structureReqs.push(
        `- Last Group: a Cool-down, with a duration you size appropriately (kept inside the total time budget).`,
      );
    }
  } else if (includeCooldown && cooldownDuration > 0) {
    structureReqs.push(`- Last Group: Cool-down (~${cooldownDuration} min)`);
  } else {
    hasCooldown = false;
    structureReqs.push(
      `- Do NOT include a Cool-down Group. End workout immediately after main blocks.`,
    );
  }

  // A cool-down is a return to baseline, not a stretching session — flexibility
  // work is its own training block, programmed and timed on its own terms.
  if (hasCooldown) {
    structureReqs.push(
      `  A cool-down returns the body to baseline: easy aerobic work tapering down, plus breathing and downregulation. Do NOT fill it with static stretching or mobility drills — that is flexibility training and belongs in its own Flexibility block, not tacked onto the end. Keep it short.`,
    );
  }

  const structureSection = `\n## Structure Requirements\nThe workout MUST follow this exact structure:\n${structureReqs.join("\n")}`;

  // How to SPEND the budget, stated as arithmetic the model can actually do.
  // "Fill 30 minutes" is not actionable on its own: a model that cannot price a
  // block in seconds has no way to tell that it has under-delivered. The
  // per-rep figures are the estimator's real baselines (repEstimator BASELINES
  // — 3.0 s default, 2.0 s for bodyweight calisthenics), so the model's tally
  // and the runtime's estimate agree rather than drifting apart.
  const budgetCheckSection = `
## Time Budget Check
Price the session before you output it, block by block:
- a sets/rounds Group costs (its value) × (everything inside it, once)
- a time-capped Group costs its cap, once
- rep work costs ~3 s per rep for loaded or unfamiliar lifts, ~2 s per rep for bodyweight calisthenics
- timed Work and Rest cost their own value
Add the blocks up. The total must land between ${budgetFloorMinutes} and ${timeAvailable} min.
If it falls short, ADD WORK to the main blocks — more exercises first (for muscle groups not yet near their per-session cap), then more sets or rounds — and re-price until it lands in the band. Do NOT close the gap by padding the warm-up or cool-down, by inflating rest periods, or by claiming a duration in a label that the nodes underneath do not actually add up to.`;

  // Build the context string with explicit Chronic vs Acute labeling
  const conditionContext = [
    `Readiness: ${readiness}`,
    injuryLimitationNotes
      ? `Chronic Profile Limitations: "${injuryLimitationNotes}"`
      : null,
    sessionNotes
      ? `Acute Session Notes (Today's condition): "${sessionNotes}"`
      : null,
  ]
    .filter(Boolean)
    .join(" | ");

  const capabilitiesSection = buildCapabilitiesSection(performanceCapabilities);
  // When the user chose a format, the Style Rule describes the format and this
  // says what to train inside it.
  const goals = (formatGoals || []).filter((g) => GOAL_LOADING[g]);
  const formatGoalSection =
    formatStyle && FORMAT_NAMES[formatStyle] && goals.length
      ? `
## Training Goal
Run the main work as ${FORMAT_NAMES[formatStyle]} (structure per the Style Rule above). The goal sets the loading inside it:
${goals.map((g) => `- ${GOAL_LOADING[g]}`).join("\n")}
${
  goals.length > 1 && formatStyle !== "tabata"
    ? `Build one ${formatStyle.toUpperCase()} block per goal, sequenced physiologically (strength and power first), each as its own top-level block.`
    : "Keep it to a single block."
}
`
      : "";

  // Placed after the Style Rule because it amends it — the paired and active
  // rest rules explicitly override the straight-set specs above them.
  const preferencesSection = buildTrainingPreferencesSection({
    restStyle,
    sessionPace,
    trainingPreferences,
    styles: orderedStyles,
  });
  const historySection = buildTrainingHistorySection(recentTrainingHistory, {
    lookbackDays: historyLookbackDays,
  });
  const muscleVolumeSection = buildMuscleVolumeSection(weeklyMuscleSets);

  // When the session belongs to a programme slot, its periodisation framework
  // constraints and surrounding-session awareness arrive pre-built (see
  // utils/programmeContext.js) and get a dedicated section — these are
  // structural constraints, not "today's condition" notes.
  const programmeSection =
    programmeContext && programmeContext.trim()
      ? `\n## Programme Context\nThis session is ONE SLOT of a longer periodised programme. The framework constraints below OVERRIDE the generic style guidance where they conflict.\n${programmeContext.trim()}\n`
      : "";

  // How close to failure each goal needs — the gap AI-written plans most often
  // leave open (Havers 2025). Strength gains are flat across a wide range of
  // RIR while hypertrophy improves nearer failure (Robinson 2024); power is
  // lost to fatigue (Pareja-Blanco 2017). See docs/training_science.md §2.
  const isBeginner =
    String(fitnessLevel || "").toLowerCase() === "beginner" ||
    ["beginner", "novice"].includes(
      String(trainingExperience || "").toLowerCase(),
    );
  const effortRules = isBeginner
    ? `Effort by goal: this user is a BEGINNER — keep every resistance set at least 3 reps short of failure (3+ RIR); technique limits the load before effort does. Power work never approaches failure.`
    : `Effort by goal (reps in reserve, RIR — how many more reps the user could have done):
- Hypertrophy: 0-3 RIR, the last set closest to failure.
- Strength: 2-4 RIR. Never to failure.
- Power / speed: never near failure — end the set when speed drops.
- Muscular endurance / circuits: 1-3 RIR.`;

  // Per-session volume: returns flatten past ~2 direct sets per exercise for
  // strength and ~11 sets per muscle per session for hypertrophy (Remmert
  // 2025). Without this the time budget gets filled with junk sets on the same
  // muscle. docs/training_science.md §2, M1.
  const volumeRules = `
## Volume Per Session
- Returns diminish past about 10 hard sets for one muscle group in a single session. Beyond that, spend remaining time on OTHER muscle groups or on a different block (e.g. a conditioning finisher), not more sets for the same muscle. If the user's focus is a single area, stop at about 15.
- Count every exercise that trains a muscle toward it, not only the one named for it: squats, deadlifts, lunges and hip thrusts all count toward the glutes; rows and pull-downs both count toward the lats.
- Strength: 3-5 working sets for the main lift and 2-3 for each secondary lift — more sets of the same lift add little.
- A "hard set" is one taken within about 4 reps of failure; warm-up and ramp-up sets do not count.`;

  // Older adults: balance + strength 3+ days a week (WHO 2020); power training
  // at least matches traditional strength work for function (2022-23 reviews),
  // and power declines fastest with age (Galpin). M3.
  const olderAdultSection =
    age != null && age >= OLDER_ADULT_AGE
      ? `
## Age Considerations (${age})
Unless injuries or today's notes rule it out, include:
- At least one fast-concentric POWER exercise at a moderate load, with full recovery between sets — e.g. sit-to-stand with a fast rise, step-up with a fast drive, medicine-ball chest throw. Lift fast, lower under control, never to failure.
- A BALANCE challenge — e.g. single-leg stance, tandem walk, slow single-leg reach, or step-ups — in the warm-up or between sets.
Keep loaded movements stable (supported or bilateral where possible) and progress gradually.
`
      : "";

  // No faff (#85): the user is short of patience for setup, not just time.
  // Placed with the other session-level constraints; validatePlan enforces it.
  const noFaffSection = noFaff
    ? `
## No-Faff Session
The user wants a hard session that is quick to set up and start, with almost no time lost to changing equipment or weights:
- Use at most 2 pieces of equipment for the whole session (e.g. an assault bike and one kettlebell), plus bodyweight. Set them up once.
- One load per implement: never ask for plate changes, a different dumbbell pair or rack adjustments. No barbell, rack or landmine work.
- Prefer simple, hard movements with no setup: burpees, kettlebell swings, goblet squats, push-ups, slam balls, sandbag work, erg intervals.
- Run the work as a circuit, EMOM, AMRAP or intervals so time goes on work, not waiting.
- Any warm-up is short and uses the same kit and movements at low intensity, leading straight into the work.
The style rules still apply within these limits.
`
    : "";

  // Time-efficient training (Iversen 2021; Zhang 2025): what to keep when the
  // session is short. Threshold matches the default auto warm-up threshold.
  const shortSessionSection =
    timeAvailable <= SHORT_SESSION_MINUTES
      ? `
## Short Session (${timeAvailable} min)
Time is the constraint, so spend it on what matters most:
- Lead with bilateral compound lifts that together cover a squat or leg press, an upper-body push and an upper-body pull.
- Where the style allows it, pair opposing muscle groups back-to-back (push with pull, upper with lower) so one muscle recovers while the other works.
- Drop sets and rest-pause only on single-joint or machine accessories — never on heavy free-weight compound lifts.
- No general warm-up before moderate-load work. If a lift will reach >=80% 1RM, prepare it with 1-3 ramp-up sets instead.
- No stretching unless the goal is mobility.
`
      : "";

  const intensityRules = `
## Intensity & Autoregulation Rules
You must adapt the volume, exercise selection, and intensity to the user's current condition:
[ ${conditionContext} ]

- If Readiness is "Low Energy" or notes indicate fatigue/injury: Lower total volume, prescribe conservative intensity targets, and avoid aggravating movements.
- If Readiness is "Feeling Great": Challenge them with progressive overload.
- If Readiness is "Normal" (and no limiting notes): Proceed with standard progression.

${effortRules}
${capabilitiesSection}
For every "Work" node, you MUST include a concise \`intensityTarget\` string written in the user's preferred format (${intensityPreference}). Load fields (\`weight\`, \`intensityPct\`) do NOT replace it: a heavy set still needs its effort cue (e.g. 'RIR 2-3'). Rest nodes use an empty string.
- Strength/Hypertrophy: %1RM, RPE, or RIR (e.g., '75% 1RM', 'RPE 8', '2 RIR').
- Bodyweight: RIR or to failure (e.g., 'Leave 1 rep in tank', 'AMRAP').
- Cardio/Ergs: Pace, Watts, RPM, or % Max HR (e.g., '2k pace + 5s', 'Zone 3', '80% effort').
- Power/Plyo: Explosive intent (e.g., 'Max bar speed', 'Max vertical height').
- Mobility/Recovery: Sensation (e.g., 'Gentle stretch', 'Focus on breathing').
- Beginners: Descriptive text (e.g., 'Challenging but doable', 'Moderate effort').
`;

  return `You are an expert fitness coach. Generate a structured workout plan as JSON.

## Profile
${age ? `Age: ${age} | ` : ""}Level: ${fitnessLevel} | Experience: ${trainingExperience || "intermediate"} | Goal: ${primaryGoal} | Style: ${orderedStyles.join(" + ")}
Target: ${targetMuscles?.join(", ") || "full body"} | Equipment: ${availableEquipment || "none"}
EQUIPMENT IS A HARD LIMIT: use only the equipment listed above, plus bodyweight and running. Machines named anywhere else in these instructions (a bike, rower, ski erg…) are examples, not a kit list: never prescribe one that isn't listed above.
${avoidedAreas?.length ? `Avoid: ${avoidedAreas.join(", ")}\n` : ""}
Time Planning: ${timeBudgetSummary}

## Node Schema
Every node requires: id (unique string), label, type, value, unit.
- type "Group": container with children[]. COUNT unit ("rounds" | "sets" | "workout") → value = repeat count. TIME unit ("s" | "m") → fixed-time container (AMRAP / time cap): value = the duration cap, children are a checklist cycled for that whole time (the runtime runs ONE countdown — it does not loop or sum them).
- A BLOCK IS NEVER A CHILD OF ANOTHER BLOCK. Each main block (and the warm-up and cool-down) is a direct child of the ROOT Group. Nesting a Group inside a Group is for STRUCTURE WITHIN one block — the window of an EMOM, the sets of an exercise — never for placing the next block. A Group's \`value\` repeats everything inside it, so a block placed inside a Group that runs 8 rounds is performed 8 times, and any duration stated in its label becomes wrong by that factor.
- CHILDREN BELONG TO GROUPS ONLY. A "Work" / "Rest" / "Warmup" / "Cooldown" node is a STEP and carries no \`children\` key, ever. Steps that follow one another are SIBLINGS in the same Group's children array — NEVER nest the next step inside the previous one. A Work node holding children is DISCARDED work: the runtime performs only the innermost node, so every prescription wrapped around it disappears without trace.
- SETS vs LOOP COUNT: a Group's \`value\` repeats its ENTIRE children list. For one exercise across MULTIPLE sets — whether identical OR a ramp/pyramid — use ONE \`Group(unit="sets", value=N)\` holding a SINGLE Work(+optional inter-set Rest) child. NEVER enumerate each set as its own repeated Work node, and NEVER both enumerate per-set children AND set value > 1 (a value=4 Group holding 4 sets = 16 sets).
- SET SCHEME (varying parameters): when reps, load, or %1RM change set-to-set (e.g. 8/6/5/5 reps, or 60/65/70/70 kg), keep the single Work child and add a \`setScheme\` object that maps each VARYING dimension to a per-set array. Allowed dimensions: \`value\` (reps OR seconds, in the node's own \`unit\`), \`weight\` (kg), \`intensityPct\` (% 1RM). Rules: (a) every array length MUST equal the Group's \`value\` (= number of sets); (b) only include dimensions that actually change — a constant parameter stays on the static field, not in setScheme; (c) the Work node's static \`value\`/\`weight\`/\`intensityPct\` MUST equal set 1 (the first array entry). For IDENTICAL sets (nothing varies), omit \`setScheme\` entirely. Example: \`{"type":"Group","label":"Bench Press","unit":"sets","value":4,"children":[{"type":"Work","label":"Bench Press","unit":"reps","value":8,"weight":60,"setScheme":{"value":[8,6,5,5],"weight":[60,65,70,70]}},{"type":"Rest","value":120,"unit":"s","autoStart":true}]}\`.
- type "Work": exercise. Timed: unit="s"|"m", value=duration. Reps: unit="reps", value=count. Calories: unit="cal", value=the calorie target.
- ERGO / CARDIO (row, bike, ski, run, swim): a CALORIE target is a real unit — "50 cal on the assault bike" is Work(label:"Assault Bike", unit:"cal", value:50), and the runtime estimates its duration from the machine's calorie rate. A DISTANCE on one of those machines is equally real: emit Work(unit="m", value=metres) and name the machine in the label ("500 m Row"), and the runtime converts it to a pace estimate. That reading needs BOTH conditions — the label names the ergo AND value >= 100 — because "m" otherwise means MINUTES ("Easy Bike", value 3, is a 3 minute spin). So a SHORT or non-ergo distance (a 20 m sled push, a 40 m farmer carry) never goes in a "m" node: keep the distance in the LABEL and prescribe the work by time ("s") or reps.
- type "Rest": DELIBERATE PHYSIOLOGICAL RECOVERY only. unit="s"|"m", value=duration. Author a Rest ONLY for: (a) inter-set rest within an exercise's Group, (b) round-boundary rest at the end of a circuit/round, (c) prescribed interval rest in HIIT/Tabata/EMOM, (d) recovery in conditioning Work+Rest pairs. Do NOT author a Rest merely to separate two DIFFERENT exercises performed back-to-back — the runtime auto-injects a hidden logistical transition for those gaps. There is no separate SetRest type.
- autoStart: true = auto-advance (use for flow-based styles). false = pause for user (default).
- UNITS: timed nodes use ONLY "s" or "m". NEVER "seconds"/"minutes"/"sec"/"min". Calorie targets use ONLY "cal". NEVER "cals"/"calories"/"kcal".
- LOAD (Work only, optional): For barbell/dumbbell strength sets, include either \`weight\` (kg, number) OR \`intensityPct\` (% 1RM, number 0–100). Emit one of these on EVERY ramped warm-up set and working set — the runtime uses them to estimate rep time accurately, and a missing or wrong load on a warm-up will be timed as a heavy grind. Skip for bodyweight / functional movements.

### 2. RECOVERY & ADJACENCY
A \`Rest\` node means deliberate physiological recovery, never "move to the next exercise". Principle: if removing the Rest would only remove the walk/setup time between two different exercises, do NOT author it — the runtime injects a hidden transition there automatically based on the user's location settings.
- Author a Rest ONLY between repetitions of the SAME exercise (inter-set), at the END of a full round/circuit (round-boundary), or as a prescribed interval rest (HIIT/Tabata/EMOM/conditioning).
- Do NOT author a Rest between two DIFFERENT back-to-back exercises (e.g. Back Squat → Bench Press in a superset, or station-to-station in a circuit). Emit the two Work nodes adjacently.
- Never emit two recovery nodes back-to-back — the compiler's adjacency filter will silently collapse them.

### 3. EXERCISE LABELS
Emit \`label\` in **human-readable Title Case** ("Back Squat", "Bench Press", "Ball Slam", "Push-up"). NEVER emit raw snake_case keys (no "ball_slam", no "turkish_getup"). The runtime resolves canonical keys from the label automatically (it lowercases, strips punctuation, drops equipment qualifiers, and matches synonyms).

The runtime recognises ${EXERCISES.length} common exercises by their standard names (e.g. Goblet Squat, Romanian Deadlift, Kettlebell Swing, Push Press, Lat Pulldown, Farmer Carry, Assault Bike) and times each rep accordingly, with velocity-based timing for Back Squat, Bench Press, Deadlift and Overhead Press when a load is given. Prefer these standard names over inventive ones; anything unrecognised is timed at a generic ~3 s per rep.

${structureSection}
${budgetCheckSection}
${volumeRules}
${shortSessionSection}${noFaffSection}${olderAdultSection}${intensityRules}
${historySection}${muscleVolumeSection}${programmeSection}
## Style Rule
${styleInstructions}
${formatGoalSection}${preferencesSection}
## Example (Component Only)
(Note: The example below is just one block to show syntax. The full plan must include all required structure components.)
${example}

## Output
Wrap in \`{"workout_plan": { ... }}\`. Root: Group(id="root", value=1, unit="workout").
${historySection ? `On the root object, alongside "children", also include a "historyNote" string: ONE short plain sentence on how today's focus responds to the Recent Training History above (omit only if history changed nothing). ` : ""}${durationConstraint}${restFilterNote} Output ONLY valid JSON.`;
};

const REPAIR_PROMPT = `You are a fitness coach. The user pasted a workout session description but it may be corrupted, incomplete, or in a non-standard format.

Interpret the text below and produce a valid structured workout plan as JSON.

## Node Schema
Every node requires: id (unique string), label, type, value, unit.
- type "Group": container with children[]. COUNT unit ("rounds" | "sets" | "workout") → value = repeat count. TIME unit ("s" | "m") → fixed-time container (AMRAP / time cap): value = the duration cap, children are a checklist cycled for that whole time (the runtime runs ONE countdown — it does not loop or sum them).
- A BLOCK IS NEVER A CHILD OF ANOTHER BLOCK. Each main block (and the warm-up and cool-down) is a direct child of the ROOT Group. Nesting a Group inside a Group is for STRUCTURE WITHIN one block — the window of an EMOM, the sets of an exercise — never for placing the next block. A Group's \`value\` repeats everything inside it, so a block placed inside a Group that runs 8 rounds is performed 8 times, and any duration stated in its label becomes wrong by that factor.
- CHILDREN BELONG TO GROUPS ONLY. A "Work" / "Rest" / "Warmup" / "Cooldown" node is a STEP and carries no \`children\` key, ever. Steps that follow one another are SIBLINGS in the same Group's children array — NEVER nest the next step inside the previous one. A Work node holding children is DISCARDED work: the runtime performs only the innermost node, so every prescription wrapped around it disappears without trace.
- type "Work": exercise. Timed: unit="s"|"m", value=duration. Reps: unit="reps", value=count. Calories: unit="cal", value=the calorie target.
- ERGO / CARDIO (row, bike, ski, run, swim): a CALORIE target is a real unit — "50 cal on the assault bike" is Work(label:"Assault Bike", unit:"cal", value:50), and the runtime estimates its duration from the machine's calorie rate. A DISTANCE on one of those machines is equally real: emit Work(unit="m", value=metres) and name the machine in the label ("500 m Row"), and the runtime converts it to a pace estimate. That reading needs BOTH conditions — the label names the ergo AND value >= 100 — because "m" otherwise means MINUTES ("Easy Bike", value 3, is a 3 minute spin). So a SHORT or non-ergo distance (a 20 m sled push, a 40 m farmer carry) never goes in a "m" node: keep the distance in the LABEL and prescribe the work by time ("s") or reps.
- type "Rest": rest period. unit="s"|"m", value=duration.
- type "Warmup" | "Cooldown": special sections.
- UNITS: timed nodes use ONLY "s" or "m". NEVER "seconds"/"minutes". Calorie targets use ONLY "cal".

## Output
Wrap in \`{"workout_plan": { ... }}\`. Root: Group(id="root", value=1, unit="workout").
Output ONLY valid JSON, nothing else.

## Text to interpret:
`;

// Interpreting a workout the user DESCRIBES in their own words, after the fact
// (#77). Distinct from REPAIR_PROMPT, which reconstructs a plan from mangled
// structured text, and from the generation prompt, which invents one.
//
// The governing difference is that this is a RECORD, not a prescription. The
// model's job is transcription, not coaching: everything it emits must trace to
// something the user actually said. A generation prompt that quietly rounds
// 8 reps to 10, or adds the warm-up the session "should" have had, produces a
// nicer plan and a false training log — and this log is what the planner reads
// to decide tomorrow's session, so an invented set becomes real fatigue it
// works around.
const LOG_WORKOUT_PROMPT = `You are transcribing a workout the user has ALREADY DONE, from their own description, into structured JSON.

You are a TRANSCRIBER, not a coach. Record exactly what they say they did.
- NEVER invent exercises, sets, reps, loads or rests they did not mention.
- NEVER add warm-ups, cool-downs, rests or transitions unless they described them.
- NEVER "improve", round or regularise their numbers. 7 reps stays 7.
- If something is ambiguous, choose the plainest reading. If a detail is absent, omit the field rather than guessing a value.

## Structure
Root: Group with id "root", label = a short session name, type "Group", unit "workout", value 1, and children[].

- "three sets of 20 burpees" -> Group(unit:"sets", value:3) containing one Work(label:"Burpees", unit:"reps", value:20).
- "bench press 80kg, 8 reps, six times" -> Group(unit:"sets", value:6) containing one Work(label:"Bench Press", unit:"reps", value:8, weight:80). The number of SETS is the Group's value; the reps are the Work's value.
- SUPERSET / circuit ("X supersetted with Y", "alternating A and B") -> ONE Group whose value is the number of rounds and whose children are the Work nodes in order. Do NOT emit a separate Group per exercise.
- REPEATED ROUNDS ("10 rounds of A, B, C") -> ONE Group with value 10 holding A, B and C ONCE. NEVER write the round out ten times — the Group's value IS the repetition, and enumerating it produces a wrong plan as well as an enormous one.
- A held or timed exercise -> Work with unit "s" or "m" and value = the duration. NEVER "seconds"/"minutes" as a unit.
- Calorie work (50 cal on the assault bike, 20 cal row) -> Work with unit "cal" and value = the calorie target. Do NOT convert it to a duration and do NOT bury it in the label — "cal" is a first-class unit and the runtime estimates the time from the machine.
- Distance work (a 400 m sprint, a 2 km row) -> there is no distance unit. If they gave a TIME, use it: Work with unit "s"/"m". If they gave only the distance, keep that figure IN THE LABEL ("400 m Sprint") with unit "reps" and value 1. Do NOT invent a duration for it, and do NOT drop it — it is work they did, and the label is what the planner reads.

## Load
- \`weight\` (number, kg) on the Work node. "80 kilos", "80kg", "80" after a barbell lift all mean weight: 80.
- Convert other units to kg (1 lb = 0.4536 kg, 1 pood = 16.38 kg) and round to 1 decimal.
- Percentages of 1RM go in \`intensityPct\` (0-100) instead. Never emit both on one node.
- Bodyweight movements carry NO weight field.
- Load that CHANGED set to set ("60, then 70, then 80") -> keep ONE Work and add \`setScheme\`: {"weight":[60,70,80]}. The array length MUST equal the Group's value, and the Work's own \`weight\` MUST equal the first entry. Same rule for reps that change: \`setScheme\`: {"value":[10,8,6]}.

## Session metadata
Also return a "meta" object:
- "title": short name for the session, e.g. "Push + deadlifts". Not a sentence.
- "activityType": one of "strength" | "run" | "ride" | "row" | "swim" | "walk" | "class" | "other". Use "strength" whenever exercises with sets/reps are described.
- "durationMinutes": integer. Use the duration they stated. If they did not state one, ESTIMATE it from the work described and set "durationEstimated": true.
- "daysAgo": integer, 0 = today. Read relative dates in the text ("yesterday" = 1, "this morning" = 0, "on Monday" = count back from today). Default 0.
- "rating": integer 1-10 ONLY if they described the effort ("brutal", "easy"). Omit otherwise.
- "notes": anything they said that the structure cannot hold (how it felt, an injury, a detail you could not place). Omit if there is nothing left over.

## Output
Wrap in {"workout_plan": { ... }, "meta": { ... }}.
Do NOT emit "id" fields — they are assigned on our side and any you write are discarded.
Keep the JSON compact: no field is needed beyond the ones named above.
Output ONLY valid JSON, nothing else.

## The user's description:
`;

const ENHANCE_PROMPT = (
  exerciseName,
  context,
) => `You are a certified personal trainer. Provide tips for the exercise "${exerciseName}".
${context?.type ? `Exercise type: ${context.type}.` : ""}
${context?.fitnessLevel ? `User fitness level: ${context.fitnessLevel}.` : ""}

Return a JSON object with these fields:
{
  "description": "A concise 1-2 sentence description of the exercise and its benefits",
  "formTips": ["tip1", "tip2", "tip3"],
  "commonMistakes": ["mistake1", "mistake2"],
  "alternatives": ["alternative1", "alternative2"]
}

Return ONLY the JSON object, no markdown fences or extra text.`;

export {
  buildPrompt,
  generateDynamicExample,
  REPAIR_PROMPT,
  ENHANCE_PROMPT,
  LOG_WORKOUT_PROMPT,
};
